import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useLocalData } from "../hooks/useLocalData";
import BottomNav from "../components/BottomNav";
import {
  Calendar, FileText, Users, ClipboardCheck,
  ChevronRight, Clock, AlertTriangle, Activity,
} from "lucide-react";

export default function ClinicianDashboard() {
  const navigate = useNavigate();
  const { profile, user, hasPermission } = useAuth();
  const { notes = [], individuals = [] } = useLocalData();

  const displayName = profile?.displayName || user?.displayName || "Clinician";
  const firstName = displayName.split(" ")[0];

  // Derive stats from existing data
  const today = new Date().toLocaleDateString();
  const todayNotes = notes.filter(n => n.date === today);
  const pendingNotes = notes.filter(n => n.status === "draft");
  const submittedNotes = notes.filter(n => n.status === "submitted" || n.status === "signed");

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const C = {
    card: {
      background: "rgba(15,23,42,0.7)",
      border: "1px solid rgba(99,179,237,0.08)",
      borderRadius: 16,
      padding: 20,
      marginBottom: 12,
    },
  };

  const quickActions = [
    {
      label: "Today's Schedule",
      icon: Calendar,
      color: "#63b3ed",
      path: "/schedule",
      count: null,
    },
    {
      label: "My Clients",
      icon: Users,
      color: "#68d391",
      path: "/caseload",
      count: individuals.length,
    },
    {
      label: "Pending Notes",
      icon: FileText,
      color: "#ecc94b",
      path: "/notes",
      count: pendingNotes.length,
    },
    {
      label: "Assessments",
      icon: ClipboardCheck,
      color: "#b794f4",
      path: "/assessments",
      count: null,
      disabled: true,
      badge: "Coming Soon",
    },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0f1a",
      color: "#e2e8f0",
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      paddingBottom: 100,
    }}>
      {/* Header */}
      <header style={{
        padding: "20px 20px 16px",
        background: "linear-gradient(135deg, rgba(15,23,42,0.9), rgba(16,28,50,0.9))",
        borderBottom: "1px solid rgba(99,179,237,0.08)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, color: "#718096", fontWeight: 600 }}>{greeting()}</div>
            <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>{firstName}</div>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#68d391",
              letterSpacing: 1.5, textTransform: "uppercase", marginTop: 4,
            }}>
              Clinician Dashboard
            </div>
          </div>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: "linear-gradient(135deg, #68d391, #38a169)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 900, color: "#0a0f1e",
          }}>
            {firstName[0]}
          </div>
        </div>
      </header>

      <main style={{ padding: 20 }}>
        {/* Today's Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Today", value: todayNotes.length, color: "#63b3ed", sub: "notes" },
            { label: "Pending", value: pendingNotes.length, color: "#ecc94b", sub: "drafts" },
            { label: "Clients", value: individuals.length, color: "#68d391", sub: "active" },
          ].map((s, i) => (
            <div key={i} style={{ ...C.card, textAlign: "center", padding: 16 }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{ fontSize: 12, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>
          Quick Actions
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {quickActions.map((action, i) => {
            const Icon = action.icon;
            return (
              <button
                key={i}
                onClick={() => !action.disabled && navigate(action.path)}
                style={{
                  ...C.card,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  cursor: action.disabled ? "default" : "pointer",
                  opacity: action.disabled ? 0.5 : 1,
                  marginBottom: 0,
                  textAlign: "left",
                  width: "100%",
                  border: "1px solid rgba(99,179,237,0.08)",
                }}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: `${action.color}12`,
                  border: `1px solid ${action.color}25`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Icon size={20} style={{ color: action.color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0" }}>{action.label}</div>
                  {action.badge && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: "#b794f4",
                      background: "rgba(183,148,244,0.1)",
                      padding: "2px 8px", borderRadius: 6,
                    }}>{action.badge}</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {action.count != null && (
                    <span style={{
                      fontSize: 14, fontWeight: 900, color: action.color,
                      fontFamily: "monospace",
                    }}>{action.count}</span>
                  )}
                  <ChevronRight size={16} style={{ color: "#4a5568" }} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Start Session Button */}
        <button
          onClick={() => navigate("/session/new")}
          style={{
            width: "100%",
            padding: "16px 0",
            borderRadius: 14,
            background: "linear-gradient(135deg, #68d391, #38a169)",
            color: "#0a0f1e",
            fontSize: 15,
            fontWeight: 800,
            border: "none",
            cursor: "pointer",
            marginTop: 20,
            boxShadow: "0 4px 20px rgba(104,211,145,0.3)",
          }}
        >
          + Start Session
        </button>

        {/* Recent Notes */}
        {notes.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: "#718096",
              textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10,
            }}>
              Recent Notes
            </div>
            {notes.slice(0, 3).map((note, i) => (
              <div key={note.id || i} style={{
                ...C.card,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {note.individualName || "Unknown Individual"}
                  </div>
                  <div style={{ fontSize: 11, color: "#718096", marginTop: 2 }}>
                    {note.serviceType || "Session"} — {note.date || "No date"}
                  </div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 8,
                  background: note.status === "submitted" || note.status === "signed"
                    ? "rgba(104,211,145,0.1)" : "rgba(236,201,75,0.1)",
                  color: note.status === "submitted" || note.status === "signed"
                    ? "#68d391" : "#ecc94b",
                  textTransform: "uppercase",
                }}>
                  {note.status || "draft"}
                </span>
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav active="home" />
    </div>
  );
}